import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { DEFAULT_IP } from 'app/utils/constant';

// Shared data hook for the three campaign quarterly list pages
// (/campaign/complete, /campaign/openregistration, /campaign/progress).
// Reads quarter/year from the URL and queries the backend by status `type`.
const useCampaignQuarterlyList = (type) => {
    const [searchParams] = useSearchParams();
    const quarter = searchParams.get('quarter') || '';
    const year = searchParams.get('year') || '';

    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(false);
        axios
            .get(DEFAULT_IP + '/webapp/memportal/getcampaignquarterlylist', {
                params: { type, quarter, year },
            })
            .then((res) => {
                if (!active) return;
                if (res.data?.code === 1) {
                    setCampaigns(res.data.data?.campaigns || []);
                } else {
                    setError(true);
                    setCampaigns([]);
                }
            })
            .catch(() => {
                if (active) {
                    setError(true);
                    setCampaigns([]);
                }
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [type, quarter, year]);

    return { campaigns, loading, error, quarter, year };
};

// After the list renders, fetch per-campaign enrollment counts
// (total_applicants / total_waitlisted / space_taken / space_available …).
// One request per campaign to /getcampaignenrollmentcount; returns a map
// keyed by campaign_id. NOTE: this endpoint nests success at code.code.
export const useCampaignEnrollmentCounts = (campaigns, queryType) => {
    const [counts, setCounts] = useState({});

    useEffect(() => {
        if (!campaigns || campaigns.length === 0) {
            setCounts({});
            return;
        }
        let active = true;
        Promise.all(
            campaigns.map((c) =>
                axios
                    .post(DEFAULT_IP + '/webapp/memportal/getcampaignenrollmentcount', {
                        campaign_id: c.campaign_id,
                        query_type: queryType,
                    })
                    .then((res) => {
                        const body = res.data || {};
                        // Tolerate both { code: 1, ... } and { code: { code: 1 }, ... }.
                        const ok = body.code === 1 || body.code?.code === 1;
                        return ok && body.data
                            ? { id: c.campaign_id, data: body.data }
                            : null;
                    })
                    .catch(() => null),
            ),
        ).then((results) => {
            if (!active) return;
            const map = {};
            results.forEach((r) => {
                if (r) map[r.id] = r.data;
            });
            setCounts(map);
        });
        return () => {
            active = false;
        };
    }, [campaigns, queryType]);

    return counts;
};

// ---- Field helpers -------------------------------------------------------

// Render a raw value, treating null/undefined as blank (keeps 0 visible).
export const cell = (value) => (value === null || value === undefined ? '' : value);

// Bilingual {en, zh} object -> string for the active language.
export const localizedName = (obj, language) => {
    if (!obj) return '';
    return (language === 'zh' ? obj.zh : obj.en) || '';
};

// Epoch ms -> DD/MM/YYYY (blank when missing/invalid).
export const formatTs = (ts) => {
    if (ts === null || ts === undefined || ts === '') return '';
    const d = new Date(Number(ts));
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

// "start-end" date range (blank when both missing).
export const formatRange = (start, end) => {
    const s = formatTs(start);
    const e = formatTs(end);
    if (!s && !e) return '';
    return `${s}-${e}`;
};

export default useCampaignQuarterlyList;
